import { FormControl, FormGroup } from '@angular/forms';
import { NamespaceInterface, PromptModuleInterface, PromptModuleApiService } from '@vendasta/ai-assistants';
import { FormArrayWithDirtyTracking } from './forms';
import { createTemplateValidator } from './forms.validators';

// Prompt Module
export type AiPromptModuleControls = {
  id: FormControl<string | null>;
  namespace: FormControl<NamespaceInterface | null>;
  instructions: FormControl<string | null>;
  managed: FormControl<boolean>;
};

export class AiPromptModuleForm extends FormGroup<AiPromptModuleControls> {
  private originalContents: string | undefined;

  constructor(opts: {
    promptModule?: PromptModuleInterface;
    contents?: string;
    promptModuleApiService: PromptModuleApiService;
  }) {
    const { promptModule, contents, promptModuleApiService } = opts;

    super({
      id: new FormControl({ value: promptModule?.id ?? null, disabled: true }),
      namespace: new FormControl({ value: promptModule?.namespace ?? null, disabled: true }),
      instructions: new FormControl(contents ?? null, {
        asyncValidators: [createTemplateValidator(promptModuleApiService)],
      }),
      managed: new FormControl({ value: promptModule?.managed ?? false, disabled: true }, { nonNullable: true }),
    });
    this.originalContents = contents;
  }

  override get dirty() {
    const currentValue = this.controls.instructions.getRawValue();
    // Treat falsy values the same (yes we could do != instead, but this way it's harder for someone to come along and change it by accident)
    return (currentValue || null) !== (this.originalContents || null);
  }

  override markAsPristine(): void {
    super.markAsPristine();
    const value = this.controls.instructions.getRawValue();
    this.originalContents = value === null ? undefined : value;
  }

  toPromptModule(localNamespace: NamespaceInterface, name?: string | null): PromptModuleInterface {
    return {
      id: this.controls.id.getRawValue() ?? undefined,
      name: name ?? undefined,
      namespace: this.controls.namespace.getRawValue() ?? localNamespace,
      managed: this.controls.managed.getRawValue() ?? false,
    };
  }

  getContent(): string | undefined {
    return this.controls.instructions.getRawValue() ?? undefined;
  }
}

export class AiPromptModuleFormArray extends FormArrayWithDirtyTracking<AiPromptModuleForm> {
  constructor(opts: {
    promptModules: PromptModuleInterface[];
    contents: Record<string, string | undefined>;
    promptModuleApiService: PromptModuleApiService;
  }) {
    const { promptModules, contents, promptModuleApiService } = opts;

    const controls = promptModules.map((pm) => {
      const content = pm.id ? contents[pm.id] : undefined;
      return new AiPromptModuleForm({
        promptModule: pm,
        contents: content,
        promptModuleApiService,
      });
    });

    if (controls.length === 0) {
      controls.push(new AiPromptModuleForm({ promptModuleApiService }));
    }
    super(controls);
  }

  getPromptModuleKeys(fallbackNamespace: NamespaceInterface): PromptModuleInterface[] {
    return this.controls
      .map((pm) => {
        return {
          id: pm.controls.id.getRawValue() ?? undefined,
          namespace: pm.controls.namespace.getRawValue() ?? fallbackNamespace,
        };
      })
      .filter((pm) => !!pm.id);
  }
}
